import { useEffect, useState } from 'react';
import { AlertTriangle, FlaskConical, HardDriveDownload, Monitor, Cpu, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  gemma4Onnx,
  isElectronEnvironment,
  saveGemma4OnnxAcknowledged,
  type Gemma4OnnxProgress,
} from '@/lib/gemma4Onnx';

interface Gemma4OnnxConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmed: () => void;
}

const Gemma4OnnxConsentDialog = ({
  open,
  onOpenChange,
  onConfirmed,
}: Gemma4OnnxConsentDialogProps) => {
  const [progress, setProgress] = useState<Gemma4OnnxProgress>(gemma4Onnx.getProgress());
  const [hasStarted, setHasStarted] = useState(gemma4Onnx.getProgress().stage !== 'idle');
  const [isPersisting, setIsPersisting] = useState(false);
  const runtimeLabel = isElectronEnvironment() ? 'Electron' : 'Browser';

  useEffect(() => {
    const listener = (nextProgress: Gemma4OnnxProgress) => {
      setProgress(nextProgress);
      if (nextProgress.stage !== 'idle') {
        setHasStarted(true);
      }
    };

    gemma4Onnx.onProgress(listener);
    return () => gemma4Onnx.offProgress(listener);
  }, []);

  const handleProceed = async () => {
    setIsPersisting(true);

    try {
      await saveGemma4OnnxAcknowledged(true);
      onConfirmed();
      setHasStarted(true);
      void gemma4Onnx.load().catch(() => {
        // The dialog displays the runtime error state through the shared progress channel.
      });
    } finally {
      setIsPersisting(false);
    }
  };

  const handleRetry = () => {
    setHasStarted(true);
    void gemma4Onnx.load().catch(() => {
      // The dialog displays the runtime error state through the shared progress channel.
    });
  };

  const progressValue = typeof progress.progress === 'number' ? progress.progress : hasStarted ? 10 : 0;
  const isBusy = isPersisting || (hasStarted && !progress.isReady && !progress.hasError);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Gemma 4 E2B (ONNX, experimental)</DialogTitle>
            <Badge variant="secondary">Experimental</Badge>
            <Badge variant="outline" className="gap-1">
              <Monitor className="h-3 w-3" />
              {runtimeLabel}
            </Badge>
          </div>
          <DialogDescription>
            This local model runs entirely in your browser or desktop app using Transformers.js and WebGPU.
          </DialogDescription>
        </DialogHeader>

        {!hasStarted ? (
          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-foreground">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-500" />
                <div className="space-y-2">
                  <p className="font-medium">Before you continue</p>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    <li>First-time download is roughly 3–4+ GB and will be cached locally.</li>
                    <li>WebGPU is required and GPU memory pressure can be high.</li>
                    <li>Text, image, and audio inputs are supported on this experimental path.</li>
                    <li>Thinking is disabled by default and first-run generation is capped to 512 new tokens.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border p-3">
                <HardDriveDownload className="mb-2 h-4 w-4 text-primary" />
                <p className="font-medium text-foreground">Large local cache</p>
                <p className="mt-1 text-xs">Downloaded ONNX shards will be cached locally for later sessions.</p>
              </div>
              <div className="rounded-lg border p-3">
                <Cpu className="mb-2 h-4 w-4 text-primary" />
                <p className="font-medium text-foreground">Recommended GPU</p>
                <p className="mt-1 text-xs">A dedicated GPU with about 8 GB VRAM or better is recommended for stability.</p>
              </div>
              <div className="rounded-lg border p-3">
                <FlaskConical className="mb-2 h-4 w-4 text-primary" />
                <p className="font-medium text-foreground">Experimental behavior</p>
                <p className="mt-1 text-xs">If loading or inference fails, the app will automatically fall back to another local/provider path.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2 rounded-lg border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-foreground">Bootstrapping Gemma 4 ONNX</p>
                  <p className="text-sm text-muted-foreground">{progress.message}</p>
                </div>
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : null}
              </div>
              <Progress value={progressValue} />
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline">Stage: {progress.stage}</Badge>
                {typeof progress.progress === 'number' ? (
                  <Badge variant="secondary">{Math.round(progress.progress)}%</Badge>
                ) : null}
                {progress.isReady ? <Badge>Ready</Badge> : null}
                {progress.hasError ? <Badge variant="destructive">Load issue</Badge> : null}
              </div>
            </div>

            {progress.hasError ? (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                {progress.error || progress.message}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                You can close this window at any time. Loading will continue in the background until the model is ready.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {!hasStarted ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPersisting}>
                Cancel
              </Button>
              <Button onClick={handleProceed} disabled={isPersisting}>
                {isPersisting ? 'Preparing…' : 'Proceed'}
              </Button>
            </>
          ) : progress.hasError ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={handleRetry}>Retry loading</Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)}>
              {progress.isReady ? 'Close' : 'Hide and continue'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Gemma4OnnxConsentDialog;